// src/features/academic/hooks/useAcademicChallenge.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext';
import challengeService from '../services/challengeService';
import { toast } from 'react-hot-toast';

/**
 * Custom Hook for Academic Challenge
 * Manages state and logic for the daily quiz challenge
 */
const useAcademicChallenge = () => {
  const { currentUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(30); // Default 30 seconds per question
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [userScore, setUserScore] = useState(0);
  const [showRules, setShowRules] = useState(false);
  
  // Constants
  const QUIZ_TIME_LIMIT = 30; // seconds per question
  const QUESTIONS_PER_DAY = 10;
  
  // Timer interval reference
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Shuffle array utility function
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);
  
  // Initialize the hook
  useEffect(() => {
    mountedRef.current = true;
    
    const initializeChallenge = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Check if the user has already completed today's challenge
        const completed = await challengeService.hasCompletedTodaysChallenge(currentUser.uid);
        
        if (mountedRef.current) {
          setHasCompletedToday(completed);
          
          // If the user has already completed today's challenge,
          // fetch their score for display
          if (completed) {
            const userScoreData = await challengeService.fetchUserScores(currentUser.uid);
            if (userScoreData) {
              // Get the most recent score
              const todayScore = userScoreData.stats?.bestScore || 0;
              setUserScore(todayScore);
            }
          } else {
            // Fetch questions for today's challenge
            const questionsData = await challengeService.fetchDailyQuestions();
            
            // Process and shuffle the questions
            const processedQuestions = questionsData.map(q => ({
              id: q.id,
              question: q.question,
              options: shuffleArray([...q.options || []]),
              correctAnswer: q.correctAnswer,
              category: q.category || 'General Knowledge',
              difficulty: q.difficulty || 'medium'
            }));
            
            setQuestions(processedQuestions);
          }
          
          setLoading(false);
        }
      } catch (err) {
        console.error('Error initializing challenge:', err);
        if (mountedRef.current) {
          setError('There was a problem loading the academic challenge. Please try again later.');
          setLoading(false);
        }
      }
    };
    
    initializeChallenge();
    
    // Cleanup
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
    };
  }, [currentUser, shuffleArray]);
  
  // Get the current question
  const getCurrentQuestion = useCallback(() => {
    return questions[currentQuestionIndex];
  }, [questions, currentQuestionIndex]);
  
  // Check if an answer is correct
  const isAnswerCorrect = useCallback((questionIndex) => {
    if (!questions[questionIndex] || !userAnswers[questionIndex]) return false;
    return userAnswers[questionIndex] === questions[questionIndex].correctAnswer;
  }, [questions, userAnswers]);
  
  // Count correct answers
  const getCorrectAnswersCount = useCallback(() => {
    return Object.keys(userAnswers).reduce((count, questionIndex) => {
      return count + (isAnswerCorrect(parseInt(questionIndex)) ? 1 : 0);
    }, 0);
  }, [userAnswers, isAnswerCorrect]);
  
  // Calculate final score
  const calculateScore = useCallback(() => {
    // Base score: 10 points per correct answer
    return getCorrectAnswersCount() * 10;
  }, [getCorrectAnswersCount]);
  
  // Start the quiz
  const startQuiz = useCallback(() => {
    setIsQuizActive(true);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setTimeLeft(QUIZ_TIME_LIMIT);
    setIsSubmitted(false);
    setShowRules(false);
    
    // Start timer
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up, auto-submit with current selection
          clearInterval(timerRef.current);
          submitAnswer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    toast.success('Challenge started! Good luck!');
  }, []);
  
  // Handle answer selection
  const handleSelectAnswer = useCallback((answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answer
    }));
  }, [currentQuestionIndex]);
  
  // Submit the current answer
  const submitAnswer = useCallback(() => {
    // Stop the timer
    clearInterval(timerRef.current);
    
    setIsSubmitted(true);
  }, []);
  
  // Move to the next question
  const handleNextQuestion = useCallback(() => {
    const nextQuestionIndex = currentQuestionIndex + 1;
    
    if (nextQuestionIndex < questions.length) {
      // Move to the next question
      setCurrentQuestionIndex(nextQuestionIndex);
      setIsSubmitted(false);
      setTimeLeft(QUIZ_TIME_LIMIT);
      
      // Restart timer
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            submitAnswer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Quiz completed
      finishQuiz();
    }
  }, [currentQuestionIndex, questions.length, submitAnswer]);
  
  // Finish the quiz and submit results
  const finishQuiz = useCallback(async () => {
    if (!currentUser) return;
    
    // Calculate score
    const finalScore = calculateScore();
    const correctAnswers = getCorrectAnswersCount();
    
    try {
      // Show loading toast
      toast.loading('Submitting your challenge results...');
      
      // Optimistically update UI
      setHasCompletedToday(true);
      setUserScore(finalScore);
      setIsQuizActive(false);
      
      // Submit score to the server
      await challengeService.submitQuizScore({
        userId: currentUser.uid,
        score: finalScore,
        correctAnswers,
        totalQuestions: questions.length
      });
      
      // Show success toast
      toast.success('Challenge completed! Your score has been recorded.');
    } catch (error) {
      console.error('Error submitting quiz:', error);
      
      // Show error toast
      toast.error('There was a problem submitting your score. Please try again.');
      
      // Revert optimistic updates
      setHasCompletedToday(false);
      setUserScore(0);
    }
  }, [currentUser, calculateScore, getCorrectAnswersCount, questions.length]);
  
  // Exit the quiz
  const handleExitQuiz = useCallback(() => {
    if (isQuizActive && !hasCompletedToday) {
      // Confirm if the user wants to exit
      if (window.confirm('Exiting now will result in zero points. Are you sure?')) {
        // Reset state
        setIsQuizActive(false);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setTimeLeft(QUIZ_TIME_LIMIT);
        setIsSubmitted(false);
        
        // Stop timer
        clearInterval(timerRef.current);
      }
    } else {
      // Just exit
      setIsQuizActive(false);
      setShowRules(false);
    }
  }, [isQuizActive, hasCompletedToday]);
  
  // Fetch user total score
  const fetchUserTotalScore = useCallback(async () => {
    if (!currentUser) return 0;
    
    try {
      const userScoreData = await challengeService.fetchUserScores(currentUser.uid);
      return userScoreData?.totalScore || 0;
    } catch (error) {
      console.error('Error fetching total score:', error);
      return 0;
    }
  }, [currentUser]);
  
  return {
    // State
    questions,
    currentQuestionIndex,
    userAnswers,
    timeLeft,
    isSubmitted,
    loading,
    error,
    hasCompletedToday,
    isQuizActive,
    userScore,
    showRules,
    
    // Constants
    QUIZ_TIME_LIMIT,
    QUESTIONS_PER_DAY,
    
    // Functions
    getCurrentQuestion,
    isAnswerCorrect,
    getCorrectAnswersCount,
    calculateScore,
    startQuiz,
    handleExitQuiz,
    handleSelectAnswer,
    submitAnswer,
    handleNextQuestion,
    fetchUserTotalScore,
    setShowRules
  };
};

export default useAcademicChallenge;